/** Generic console batch-operations client.
 *
 *  `POST /v2/console/<resource>/batch/` runs a named action over a selection of
 *  rows. Two selection modes:
 *   - `{ mode: 'ids', ids }`     — the explicitly ticked rows;
 *   - `{ mode: 'all_matching' }` — every row the current list query matches. The
 *     server re-runs the SAME filter it uses for the list, so the caller MUST
 *     forward the grid's current list query string (`filterQuery`) so the two
 *     resolve the identical set.
 *
 *  Every batch op is enqueued on the background-task framework: a 202 means
 *  "queued", not "done". Track progress in Background Tasks. When `capped` is
 *  true only the first `processed` of `matched` rows were queued.
 *
 *  Hand-authored (console convention: feature modules never depend on the
 *  generated types); uses the shared axios wrappers from `./client`.
 */
import { apiPost } from './client';

/** Which rows a batch action targets. `all_matching` is resolved server-side
 *  from the forwarded list query, so the client never enumerates the ids.
 *  Ids are numbers for integer-PK resources, strings for UUID-PK resources
 *  (event_delivery_logs). */
export type BatchSelection =
  | { mode: 'ids'; ids: Array<number | string> }
  | { mode: 'all_matching' };

/** 202 payload: the task was queued on the background-task framework. */
export interface BatchResponse {
  /** Background-task id — look it up in Background Tasks. */
  task_id: string;
  /** Task type key (e.g. the delete task). */
  task_type: string;
  /** How many rows the selection resolved to. */
  matched: number;
  /** How many were actually queued (== matched unless `capped`). */
  processed: number;
  /** True when `matched` exceeded the server cap and only the first
   *  `processed` were queued. */
  capped: boolean;
}

/** POST `/v2/console/<resource>/batch/<?filterQuery>`.
 *
 *  `filterQuery` is the grid's current list query string (search + filters +
 *  tenant + ordering) WITHOUT a leading `?`; it is appended verbatim so the
 *  backend's `all_matching` resolves the same set the user is looking at. It is
 *  irrelevant for `mode: 'ids'` but harmless to send. */
export function runBatch(
  resource: string,
  action: string,
  selection: BatchSelection,
  params?: Record<string, unknown>,
  filterQuery?: string,
): Promise<BatchResponse> {
  const qs = filterQuery ? `?${filterQuery}` : '';
  return apiPost<BatchResponse>(`/${resource}/batch/${qs}`, {
    action,
    selection,
    ...(params ? { params } : {}),
  });
}
